import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import "./KanbanBoard.css";
import { CircularProgress } from "@mui/material";
import { FiUser, FiClock } from "react-icons/fi";
import { GoBug } from "react-icons/go";
import FilterDropdown from "./Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "./Common Components/useCurrentProject";

const STATUS_COLORS = {
    'To Do': '#64748b', 'Dev In Progress': '#2196f3', 'Dev Completed': '#00bcd4',
    'In Review': '#3f51b5', 'QA In Progress': '#ff9800', 'QA Pass': '#4caf50',
    'QA Fail': '#f44336', 'In QA': '#ff9800', 'Done': '#4caf50', 'Closed': '#78909c',
};

const getStatusColor = (s) => STATUS_COLORS[s] || '#7c5cfc';

const KanbanBoard = () => {
    const [sprints, setSprints] = useState([]);
    const [kanbanData, setkanbanData] = useState({});
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useCurrentProject();
    const [currentSprint, setCurrentSprint] = useState('');
    const [draggedTicket, setDraggedTicket] = useState(null);
    const [dragOverCol, setDragOverCol] = useState(null);
    const [ticketStatus, setTicketStatus] = useState([]);
    const [loader, setLoader] = useState(false);
    const [loading, setLoading] = useState(false);

    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

    const getProjects = useCallback(() => {
        fetch(`${API_BASE_URL}core/project/`, { method: 'GET', headers })
            .then(res => res.json())
            .then(data => {
                if (data.status === "success") {
                    setProjects(data.data);
                    setCurrentProject(prev => resolveProject(data.data, prev));
                }
            }).catch(err => console.error(err));

        fetch(`${API_BASE_URL}core/status/`, { method: 'GET', headers })
            .then(res => res.json())
            .then(data => { if (data.status === "success") setTicketStatus(data.data); })
            .catch(err => console.error(err));
    }, []);

    const getSprints = useCallback((projectId) => {
        fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${projectId}`, { method: 'GET', headers })
            .then(res => res.json())
            .then(data => {
                if (data.status === "success" && Array.isArray(data.data)) {
                    const sorted = data.data.sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
                    setSprints(sorted);
                    if (sorted.length > 0 && !currentSprint) setCurrentSprint(sorted[0].id);
                }
            }).catch(err => console.error(err));
    }, []);

    const getKanbanData = useCallback(async (sprintId) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}ticketing/kanban/board/?sprint_id=${sprintId}`, { method: 'GET', headers });
            const data = await res.json();
            if (data.status === "success") setkanbanData(data.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    const handleDragStart = (e, ticket, status) => {
        setDraggedTicket({ ...ticket, originalStatus: status });
        e.dataTransfer.setData('application/json', JSON.stringify(ticket));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, status) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverCol(status);
    };

    const handleDragLeave = () => setDragOverCol(null);

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        setDragOverCol(null);
        if (!draggedTicket || draggedTicket.originalStatus === newStatus) { setDraggedTicket(null); return; }

        setLoader(true);
        try {
            const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
                method: 'PUT', headers,
                body: JSON.stringify({ id: draggedTicket.id, sprint_id: currentSprint, status_id: ticketStatus?.find(s => s.ticket_status === newStatus)?.id }),
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast(`Moved to ${newStatus}`);
                setkanbanData(prev => {
                    const updated = { ...prev };
                    updated[draggedTicket.originalStatus] = (updated[draggedTicket.originalStatus] || []).filter(t => t.id !== draggedTicket.id);
                    if (!updated[newStatus]) updated[newStatus] = [];
                    updated[newStatus].push({ ...draggedTicket, status: newStatus });
                    return updated;
                });
            } else showErrorToast(data.message || "Failed to update");
        } catch (err) { showErrorToast("Failed to update"); }
        finally { setLoader(false); setDraggedTicket(null); }
    };

    useEffect(() => { if (currentProject) getSprints(currentProject); else setSprints([]); }, [currentProject]);
    useEffect(() => { if (currentSprint) getKanbanData(currentSprint); else setkanbanData({}); }, [currentSprint]);
    useEffect(() => { getProjects(); }, []);

    // Total counts Story + Epic only; Bug cards are surfaced separately
    const allCards = Object.values(kanbanData).flatMap(arr => arr || []);
    const bugTotal = allCards.filter(t => t.ticket_type === 'Bug').length;
    const totalTickets = allCards.length - bugTotal;
    const sprintName = sprints.find(s => String(s.id) === String(currentSprint))?.sprint_name || '';

    return (
        <div className="kb-page">
            {/* Header */}
            <div className="kb-header">
                <div className="kb-header__left">
                    <h1 className="kb-header__title">Kanban Board</h1>
                    <FilterDropdown label="Project" value={currentProject} options={projects} onChange={setCurrentProject} renderLabel={o => o?.project_name || ''} getValue={o => String(o?.id)} />
                    <FilterDropdown label="Sprint" value={currentSprint} options={sprints} onChange={setCurrentSprint} renderLabel={o => o?.sprint_name || ''} getValue={o => String(o?.id)} />
                </div>
                {sprintName && (
                    <span className="kb-header__info">
                        {sprintName} · {totalTickets} ticket{totalTickets !== 1 ? 's' : ''} · {bugTotal} bug{bugTotal !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Board */}
            {loading ? (
                <div className="kb-loading"><CircularProgress size={30} sx={{ color: 'var(--accent)' }} /></div>
            ) : Object.keys(kanbanData).length === 0 ? (
                <div className="kb-empty">Select a project and sprint to view the board</div>
            ) : (
                <div className="kb-board">
                    {Object.entries(kanbanData).map(([status, tickets]) => {
                        const color = getStatusColor(status);
                        const isDragOver = dragOverCol === status;
                        return (
                            <div
                                key={status}
                                className={`kb-col ${isDragOver ? 'kb-col--drag-over' : ''}`}
                                onDragOver={(e) => handleDragOver(e, status)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, status)}
                            >
                                <div className="kb-col__header">
                                    <span className="kb-col__dot" style={{ background: color }} />
                                    <span className="kb-col__title">{status}</span>
                                    <span className="kb-col__count">{tickets?.length || 0}</span>
                                </div>
                                <div className="kb-col__cards">
                                    {(tickets || []).map(ticket => {
                                        const tCode = ticket.parent_code || ticket.child_code || ticket.bug_code || '';
                                        const tType = ticket.ticket_type || 'Story';
                                        const isDragging = draggedTicket?.id === ticket.id;
                                        return (
                                            <div
                                                key={ticket.id}
                                                className={`kb-card ${isDragging ? 'kb-card--dragging' : ''}`}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, ticket, status)}
                                                style={{ borderLeftColor: color }}
                                            >
                                                <div className="kb-card__top">
                                                    {tCode && <span className="kb-card__code">{tCode}</span>}
                                                    <span className={`sl2-type-badge sl2-type-badge--${tType.toLowerCase()}`} style={{ fontSize: '0.58rem', padding: '1px 6px' }}>{tType}</span>
                                                </div>
                                                <div className="kb-card__title">{ticket.short_description}</div>
                                                <div className="kb-card__footer">
                                                    {ticket.assigned_to && (
                                                        <span className="kb-card__assign"><FiUser size={10} /> {ticket.assigned_to}</span>
                                                    )}
                                                    {ticket.priority_name && (
                                                        <span className={`kb-card__prio ${ticket.priority_name === 'High' ? 'kb-card__prio--high' : ticket.priority_name === 'Low' ? 'kb-card__prio--low' : ''}`}>{ticket.priority_name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {isDragOver && <div className="kb-drop-zone">Drop here</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Loading overlay for drag operations */}
            {loader && <div className="kb-loader-overlay"><CircularProgress size={28} sx={{ color: '#fff' }} /></div>}
        </div>
    );
};

export default KanbanBoard;
